import { Add } from "@mui/icons-material";
import { Fab, Stack } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { parseAsString } from "nuqs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import GaiaDataList from "~/modules/app/components/GaiaDataList";
import GaiaPageGridComponent from "~/modules/app/components/GaiaPageGridComponent";
import GaiaPageLayoutColumn from "~/modules/app/components/GaiaPageLayoutColumn";
import GaiaTableLinkCell from "~/modules/app/components/GaiaTableLinkCell";
import ErrorScreen from "~/modules/app/screens/ErrorScreen";
import SearchInput from "~/shared/components/SearchInput";
import { useDebouncedValue } from "~/shared/hooks/useDebouncedValue";
import { useGridQueryState } from "~/shared/hooks/useGridQueryState";

import useCourseListRoute_GetAll from "./api/useCourseListRoute_GetAll";

export default function CourseListRoute() {
    const { t } = useTranslation("course");

    const [queryState, setQueryState, gridModelProps] = useGridQueryState({
        q: parseAsString.withDefault(""),
    });

    const debouncedQ = useDebouncedValue(queryState.q);

    const params = useMemo(
        () => ({
            q: debouncedQ || undefined,
            page: queryState.page,
            pageSize: queryState.pageSize,
            sortBy: queryState.sortBy,
            sortOrder: queryState.sortOrder,
        }),
        [debouncedQ, queryState],
    );

    const { data, loading, error, total } = useCourseListRoute_GetAll(params);

    const handleParameterChange = <T,>(key: string, value: T) => {
        setQueryState({ [key]: value, page: 0 } as Parameters<typeof setQueryState>[0]);
    };

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: "name",
                headerName: t("courseDataGrid.headerName"),
                flex: 1,
            },
            {
                field: "description",
                headerName: t("courseDataGrid.headerDescription"),
                flex: 1,
            },
            {
                type: "actions",
                field: "id",
                headerName: "",
                maxWidth: 40,
                renderCell: props => <GaiaTableLinkCell value={props.value} />,
            },
        ],
        [t],
    );

    if (error) {
        return <ErrorScreen error={error} />;
    }

    return (
        <GaiaPageLayoutColumn maxWidth="xl">
            <GaiaPageGridComponent
                title={t("courseListScreen.title")}
                actions={
                    <Fab component={Link} to="new" color="primary">
                        <Add />
                    </Fab>
                }
            >
                <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
                    <SearchInput
                        label={t("courseDataGrid.filterSearch")}
                        value={queryState.q}
                        onChange={value => handleParameterChange("q", value)}
                    />
                </Stack>

                <GaiaDataList
                    data={data || []}
                    loading={loading}
                    error={error}
                    totalRows={total || 0}
                    columns={columns}
                    gridModels={gridModelProps}
                />
            </GaiaPageGridComponent>
        </GaiaPageLayoutColumn>
    );
}
